import posthog from 'posthog-js';

import { ENV } from '../app/env';
import { type ContractType } from '../types/contract';
import { type GoodsType } from '../types/goods';
import { type PlantTypeText } from '../types/plant';
import {
  type PlaydeckEventMethod,
  type PlaydeckEventPayload,
} from '../types/playdeck';
import { type ResourceTypeText } from '../types/resource';

import { checkIfEntityObj } from './checkIfObjectEmpty';
import { getContractCommonType } from './contract';
import { setCoinSpend } from './tgem';

class Playdeck {
  private static parent = window.parent.window;

  static postMessage(
    playdeck: unknown,
    targetOrigin: string,
    transfer?: Transferable[] | undefined,
  ): void {
    this.parent.postMessage({ playdeck }, targetOrigin, transfer);
  }

  static requestPayment({
    amount,
    description,
    photoUrl,
    externalId,
    isTest = ENV.IS_DEV,
  }: {
    amount: number;
    description: string;
    photoUrl?: string;
    externalId: string;
    isTest?: boolean;
  }): void {
    this.postMessage(
      {
        method: 'requestPayment',
        value: { amount, description, photoUrl, externalId, isTest },
      },
      '*',
    );
  }

  static getPlaydeckState(): void {
    this.postMessage({ method: 'getPlaydeckState' }, '*');
  }

  static gameEnd(): void {
    this.postMessage({ method: 'gameEnd' }, '*');
  }

  static getPaymentInfo({ externalId }: { externalId: string }): void {
    this.postMessage({ method: 'getPaymentInfo', value: { externalId } }, '*');
  }

  static sendTonTransaction({ amount }: { amount: number }): void {
    this.postMessage(
      {
        method: 'sendTonTransaction',
        value: {
          amount, // 0.1 TON (not nanoton)
        },
      },
      '*',
    );
  }

  static showAd(): void {
    this.postMessage({ method: 'showAd' }, '*');
  }

  static generateListener(
    callbacks: Partial<Record<PlaydeckEventMethod, (value: unknown) => void>>,
  ): ({ data }: MessageEvent<unknown>) => void {
    const listener = ({ data }: MessageEvent<unknown>): void => {
      // console.log('playdeck event');
      // console.log(data);

      if (!(checkIfEntityObj(data) && 'playdeck' in data)) {
        return;
      }

      const playdeck = data?.playdeck;

      if (checkIfEntityObj(playdeck) && 'method' in playdeck) {
        const method = playdeck?.method as string;
        const value =
          'value' in playdeck ? (playdeck.value as unknown) : undefined;

        if (method in callbacks) {
          const callback = callbacks[method as keyof typeof callbacks]!;
          callback(value);
        }
      }
    };

    return listener;
  }

  static sendAnalitics(value: PlaydeckEventPayload): void {
    this.postMessage({ method: 'sendAnalytics', value }, '*');
    posthog.capture(value.name, {
      ...value.event_properties,
      ...value.user_properties,
    });
  }
}

class SendAnalitics extends Playdeck {
  static buildDone({
    user_properties = {},
    ...args
  }: {
    type: 'resources' | 'materials' | 'goods';
    item: ResourceTypeText | PlantTypeText | GoodsType;
    item_id: string;
    item_level: number;
    required_level?: number;
    user_properties?: Record<string, unknown>;
  }): void {
    Playdeck.sendAnalitics({
      type: 'custom_event',
      name: 'build_done',
      user_properties,
      event_properties: {
        name: 'build_done',
        reruired_level: 1,
        ...args,
      },
    });
  }

  static contractClose({
    user_properties = {},
    ...args
  }: {
    reason: 'done' | 'reject';
    subtype: ContractType | 'productOfDay';
    reward?: { [field: string]: unknown }[];
    user_properties?: Record<string, unknown>;
  }): void {
    const type = getContractCommonType(args.subtype);

    Playdeck.sendAnalitics({
      type: 'custom_event',
      name: 'contract_close',
      user_properties,
      event_properties: {
        type,
        ...args,
      },
    });
  }

  static chestApply({
    user_properties = {},
    ...args
  }: {
    stars: number | null;
    reward: { [field: string]: unknown }[];
    use_up: boolean;
    user_properties?: Record<string, unknown>;
  }): void {
    Playdeck.sendAnalitics({
      type: 'custom_event',
      name: 'container_open',
      user_properties,
      event_properties: args,
    });
  }

  static leaderboardWin({
    user_properties = {},
    ...args
  }: {
    league: string;
    league_num: number;
    place: number;
    reward: { [field: string]: unknown }[];
    user_properties?: Record<string, unknown>;
  }): void {
    Playdeck.sendAnalitics({
      type: 'custom_event',
      name: 'leaderboard_win',
      user_properties,
      event_properties: args,
    });
  }

  static shareGame({
    user_properties = {},
    ...args
  }: {
    hasSent?: boolean;
    placement: string;
    user_properties?: Record<string, unknown>;
  }): void {
    Playdeck.sendAnalitics({
      type: 'custom_event',
      name: 'share_game_ingame_button',
      user_properties,
      event_properties: {
        name: 'share_game_ingame_button',
        hasSent: false,
        ...args,
      },
    });
  }

  static rollCraftPlay({
    user_properties = {},
    ...args
  }: {
    multiplier: number;
    reward_type: string;
    reward: { [field: string]: unknown }[];
    user_properties?: Record<string, unknown>;
  }): void {
    Playdeck.sendAnalitics({
      type: 'custom_event',
      name: 'roll_craft_play',
      user_properties,
      event_properties: args,
    });
  }

  static purchaseAttempt({
    user_properties = {},
    ...args
  }: {
    item: string;
    item_id: string;
    price: number;
    currency: string;
    user_properties?: Record<string, unknown>;
  }): void {
    Playdeck.sendAnalitics({
      type: 'custom_event',
      name: 'purchase_attempt',
      user_properties,
      event_properties: args,
    });
  }

  static purchasePending({
    user_properties = {},
    ...args
  }: {
    item: string;
    item_id: string;
    price: number;
    currency: string;
    user_properties?: Record<string, unknown>;
  }): void {
    Playdeck.sendAnalitics({
      type: 'custom_event',
      name: 'purchase_pending',
      user_properties,
      event_properties: args,
    });
  }

  static purchaseSuccessfull({
    user_properties = {},
    ...args
  }: {
    item: string;
    item_id: string;
    price: number;
    currency: string;
    user_properties?: Record<string, unknown>;
  }): void {
    Playdeck.sendAnalitics({
      type: 'custom_event',
      name: 'purchase_successful',
      user_properties,
      event_properties: args,
    });
  }

  static coinSpend({
    user_properties = {},
    ...args
  }: {
    amount: number;
    type: string;
    item_name: string;
    user_properties?: Record<string, unknown>;
  }): void {
    setCoinSpend(args.amount);
    Playdeck.sendAnalitics({
      type: 'custom_event',
      name: 'gem_spent',
      user_properties,
      event_properties: args,
    });
  }

  static getVipStatus({
    user_properties = {},
    ...args
  }: {
    level: number;
    type: 'free' | 'paid';
    user_properties?: Record<string, unknown>;
  }): void {
    Playdeck.sendAnalitics({
      type: 'custom_event',
      name: 'vip_club',
      user_properties,
      event_properties: args,
    });
  }

  static sessionFinished({
    user_properties = {},
    ...args
  }: {
    user_balance: Record<string, unknown>;
    star_points: number;
    t_gems_spend: number;
    partners: number;
    arena_score: number;
    session_length: number;
    level: number;
    vip: boolean;
    capitalizations: number;
    wallet_connect: boolean;
    user_properties?: Record<string, unknown>;
  }): void {
    Playdeck.sendAnalitics({
      type: 'custom_event',
      name: 'session_finished',
      user_properties,
      event_properties: args,
    });
  }
}

export { Playdeck, SendAnalitics };
